
import { useState } from "react";
import { X, Plus, Pencil, Trash2, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import * as contatoService from "../services/contato.service";
import type { ContatoProjeto } from "../services/contato.service";
import { toast } from "../lib/toast";

interface ContatosDrawerProps {
  open: boolean;
  onClose: () => void;
  projetoId: string;
  canEdit: boolean;
  contatos: ContatoProjeto[];
  onContatos: (contatos: ContatoProjeto[]) => void;
}

const EMPTY: Omit<ContatoProjeto, "id"> = {
  nome: "",
  cargo: "",
  empresa: "",
  telefone: "",
  email: "",
};

export function ContatosDrawer({
  open,
  onClose,
  projetoId,
  canEdit,
  contatos,
  onContatos,
}: ContatosDrawerProps) {
  const [form, setForm] = useState<Omit<ContatoProjeto, "id">>(EMPTY);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const { t } = useTranslation('common');

  if (!open) return null;

  const resetForm = () => {
    setForm(EMPTY);
    setEditingId(null);
  };

  const handleEdit = (c: ContatoProjeto) => {
    setEditingId(c.id);
    setForm({
      nome: c.nome,
      cargo: c.cargo || "",
      empresa: c.empresa || "",
      telefone: c.telefone || "",
      email: c.email || "",
    });
  };

  const handleSave = async () => {
    if (!form.nome.trim()) {
      toast.error(t('contacts_drawer.error_name_required'));
      return;
    }
    setSaving(true);
    try {
      if (editingId !== null) {
        const updated = await contatoService.atualizarContato(projetoId, editingId, form);
        onContatos(contatos.map((c) => (c.id === editingId ? updated : c)));
        toast.success(t('contacts_drawer.success_updated'));
      } else {
        const created = await contatoService.criarContato(projetoId, form);
        onContatos([...contatos, created]);
        toast.success(t('contacts_drawer.success_added'));
      }
      resetForm();
    } catch {
      toast.error(t('contacts_drawer.error_save'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await contatoService.excluirContato(projetoId, id);
      onContatos(contatos.filter((c) => c.id !== id));
      toast.success(t('contacts_drawer.success_removed'));
      if (editingId === id) resetForm();
    } catch {
      toast.error(t('contacts_drawer.error_remove'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-background rounded-xl shadow-xl border border-border w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">{t('contacts_drawer.title')}</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-muted rounded-md transition-colors"
          >
            <X size={18} className="text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Lista de contatos */}
          {contatos.length === 0 && (
            <p className="text-sm text-muted-foreground italic text-center py-6 px-6">
              {t('contacts_drawer.empty')}
            </p>
          )}

          {contatos.length > 0 && (
            <div className="px-6 pt-4 space-y-2">
              {contatos.map((c) => (
                <div
                  key={c.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border ${
                    editingId === c.id
                      ? "border-blue-400 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700"
                      : "border-border bg-muted/30"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground text-sm">{c.nome}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-xs text-muted-foreground">
                      {c.cargo && <span>{c.cargo}</span>}
                      {c.empresa && <span>{c.empresa}</span>}
                      {c.telefone && <span>{c.telefone}</span>}
                      {c.email && <span>{c.email}</span>}
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleEdit(c)}
                        className="p-1.5 hover:bg-muted rounded transition-colors"
                        title="Editar"
                      >
                        <Pencil size={14} className="text-muted-foreground" />
                      </button>
                      <button
                        onClick={() => handleDelete(c.id)}
                        disabled={deletingId === c.id}
                        className="p-1.5 hover:bg-red-100 rounded transition-colors disabled:opacity-50"
                        title="Remover"
                      >
                        <Trash2 size={14} className="text-red-500" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Formulário */}
          {canEdit && (
            <div className="px-6 py-4">
              <div className="border border-border rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium text-foreground">
                  {editingId !== null ? t('contacts_drawer.form_edit') : t('contacts_drawer.form_add')}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                      {t('contacts_drawer.field_name')} <span className="text-red-500">{t('contacts_drawer.required')}</span>
                    </label>
                    <input
                      type="text"
                      value={form.nome}
                      onChange={(e) => setForm({ ...form, nome: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                      placeholder={t('contacts_drawer.placeholder_name')}
                      maxLength={200}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{t('contacts_drawer.field_role')}</label>
                    <input
                      type="text"
                      value={form.cargo || ""}
                      onChange={(e) => setForm({ ...form, cargo: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                      placeholder={t('contacts_drawer.placeholder_role')}
                      maxLength={200}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{t('contacts_drawer.field_company')}</label>
                    <input
                      type="text"
                      value={form.empresa || ""}
                      onChange={(e) => setForm({ ...form, empresa: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                      placeholder={t('contacts_drawer.placeholder_company')}
                      maxLength={200}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{t('contacts_drawer.field_phone')}</label>
                    <input
                      type="tel"
                      value={form.telefone || ""}
                      onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                      placeholder={t('contacts_drawer.placeholder_phone')}
                      maxLength={50}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{t('contacts_drawer.field_email')}</label>
                    <input
                      type="email"
                      value={form.email || ""}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                      placeholder={t('contacts_drawer.placeholder_email')}
                      maxLength={255}
                    />
                  </div>
                </div>

                <div className="flex gap-2 justify-end">
                  {editingId !== null && (
                    <button
                      onClick={resetForm}
                      className="px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted rounded-md transition-colors"
                    >
                      {t('contacts_drawer.cancel')}
                    </button>
                  )}
                  <button
                    onClick={handleSave}
                    disabled={saving || !form.nome.trim()}
                    className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {editingId !== null ? (
                      <>
                        <Check size={14} />
                        {t('contacts_drawer.save')}
                      </>
                    ) : (
                      <>
                        <Plus size={14} />
                        {t('contacts_drawer.add')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-border flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-foreground bg-muted hover:bg-muted/80 rounded-md transition-colors"
          >
            {t('contacts_drawer.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
